import { Lock, Camera, Flower2, UtensilsCrossed, Building2, Music, Video, Sparkles } from 'lucide-react'
import RotatingWord from '@/components/RotatingWord'

// Static decorative mock of the for-nonprofits hero: a browser window framing the
// "List Your … Fundraiser Free" landing. Not interactive (aside from the rotating word).
const PLATFORMS = [
  { name: 'Givebutter', dot: 'bg-amber-400' },
  { name: 'Give Lively', dot: 'bg-emerald-500' },
  { name: 'GoFundMe', dot: 'bg-emerald-500' },
]

// Supporter avatars (free-to-use portraits stored in public/avatars).
const AVATARS = [
  '/avatars/avatar-1.jpg',
  '/avatars/avatar-2.jpg',
  '/avatars/avatar-3.jpg',
  '/avatars/avatar-4.jpg',
  '/avatars/avatar-5.jpg',
  '/avatars/avatar-6.jpg',
  '/avatars/avatar-7.jpg',
  '/avatars/avatar-8.jpg',
]

// Vendor logo marks — category glyphs standing in for vendor brands.
const VENDOR_LOGOS = [
  { Icon: Camera, className: 'bg-primary/10 text-primary' },
  { Icon: Flower2, className: 'bg-rose-100 text-rose-600' },
  { Icon: UtensilsCrossed, className: 'bg-amber-100 text-amber-700' },
  { Icon: Building2, className: 'bg-zinc-100 text-zinc-600' },
  { Icon: Music, className: 'bg-violet-100 text-violet-600' },
  { Icon: Video, className: 'bg-sky-100 text-sky-600' },
  { Icon: Sparkles, className: 'bg-emerald-100 text-emerald-600' },
]

export default function NonprofitHeroPreview() {
  return (
    <div className="relative">
      <div className="overflow-hidden rounded-2xl border border-black/10 bg-card shadow-xs">
        {/* Browser chrome */}
        <div className="flex items-center gap-3 border-b border-black/5 bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-1.5">
            <span className="size-3 rounded-full bg-[#ff5f57]" />
            <span className="size-3 rounded-full bg-[#febc2e]" />
            <span className="size-3 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex flex-1 items-center gap-1.5 rounded-md bg-card px-3 py-1 ring-1 ring-black/5">
            <Lock className="size-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">galaseason.com</span>
          </div>
        </div>

        {/* Page content */}
        <div className="space-y-5 p-6">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">For nonprofits</p>
            <h3 className="mt-1.5 font-serif text-4xl font-semibold tracking-tight">
              <span className="block">List Your <RotatingWord /></span>
              <span className="block">Fundraiser Free</span>
            </h3>
          </div>

          {/* Works-with-any-platform card */}
          <div className="rounded-xl border border-black/10 bg-muted/20 p-4">
            <p className="text-sm text-foreground">Use any platform you like for ticket sales, or use ours for free.</p>
            <p className="mt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Works with all other platforms</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {PLATFORMS.map((p) => (
                <span
                  key={p.name}
                  className="flex items-center gap-1.5 rounded-full border border-black/10 bg-card px-3 py-1 text-sm font-medium"
                >
                  <span className={`size-2 rounded-full ${p.dot}`} />
                  {p.name}
                </span>
              ))}
            </div>
          </div>

          {/* Supporters */}
          <div>
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {AVATARS.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    width={32}
                    height={32}
                    className="size-8 rounded-full object-cover ring-2 ring-card"
                  />
                ))}
              </div>
              <span className="text-sm font-semibold text-primary">+9k</span>
            </div>
            <h4 className="mt-3 font-serif text-xl font-semibold tracking-tight text-balance">
              Expand your supporters and drive more donations and sales to your events.
            </h4>
          </div>

          {/* Vendors */}
          <div>
            <div className="flex -space-x-2">
              {VENDOR_LOGOS.map(({ Icon, className }, i) => (
                <span
                  key={i}
                  className={`flex size-8 items-center justify-center rounded-full ring-2 ring-card ${className}`}
                >
                  <Icon className="size-4" />
                </span>
              ))}
            </div>
            <p className="mt-3 font-serif text-xl font-semibold tracking-tight text-balance">
              Connect with trusted vendors to bring every detail of the night to life.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
